import { PSYPACT_STATES, LUMINA_INPERSON_STATES, C } from './constants'

// ── Currency ──────────────────────────────────────────────────────────────────
export const fmt = (cents) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`

export const fmtFull = (cents) =>
  `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

// ── Dates ─────────────────────────────────────────────────────────────────────
export const today = () => new Date().toISOString().split('T')[0]

export const daysSince = (dateStr) => {
  if (!dateStr) return null
  return Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24))
}

export const daysUntil = (dateStr) => {
  if (!dateStr) return null
  return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24))
}

export const monthLabel = (iso) =>
  iso ? new Date(iso).toISOString().slice(0, 7) : '—'

// ── Assessment Severity ───────────────────────────────────────────────────────
export const phqSev = (s) => {
  if (s <= 4)  return { l: 'Minimal',           c: '#1D9E75' }
  if (s <= 9)  return { l: 'Mild',              c: '#E6A817' }
  if (s <= 14) return { l: 'Moderate',          c: '#D4721A' }
  if (s <= 19) return { l: 'Moderately Severe', c: '#C0392B' }
  return             { l: 'Severe',             c: '#922B21' }
}

export const gadSev = (s) => {
  if (s <= 4)  return { l: 'Minimal',  c: '#1D9E75' }
  if (s <= 9)  return { l: 'Mild',     c: '#E6A817' }
  if (s <= 14) return { l: 'Moderate', c: '#D4721A' }
  return             { l: 'Severe',   c: '#C0392B' }
}

// ── PSYPACT / Serviceability ──────────────────────────────────────────────────
export const getServiceability = (state, modality) => {
  if (!state) return null
  if (modality === 'in-person') {
    return LUMINA_INPERSON_STATES.has(state)
      ? { ok: true,  label: 'In-person available',                          color: C.tealGreen }
      : { ok: false, label: 'No in-person practice — consider virtual',     color: '#D4721A' }
  }
  return PSYPACT_STATES.has(state)
    ? { ok: true,  label: 'PSYPACT state — virtual eligible',               color: C.tealGreen }
    : { ok: false, label: 'Non-PSYPACT state — verify licensure',           color: '#D4721A' }
}

// ── Anonymous ID generation (preview/mock — production IDs come from API: LTA-XXXX) ──
const ANON_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
export const genAnonId = (existingCount) => {
  let n = Math.abs((existingCount + 12_341) % ANON_ALPHABET.length ** 4)
  let suffix = ''
  for (let i = 0; i < 4; i++) {
    suffix = ANON_ALPHABET[n % ANON_ALPHABET.length] + suffix
    n = Math.floor(n / ANON_ALPHABET.length)
  }
  return `LTA-${suffix}`
}

// ── Token generation ──────────────────────────────────────────────────────────
export const genToken = (clientId, type) =>
  `lta_${clientId}_${type}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`

// ── Badge status colors ───────────────────────────────────────────────────────
export const BADGE_STYLES = {
  paid:           { bg: '#E6F4F1', color: '#1D6B6B', border: '#2A7F7F' },
  sent:           { bg: '#E8F0F7', color: '#1F4D78', border: '#2E74B5' },
  draft:          { bg: '#F5F0E8', color: '#666666', border: '#D5CFC4' },
  overdue:        { bg: '#FCE8E8', color: '#B03A3A', border: '#D9534F' },
  processing:     { bg: '#FFF3E0', color: '#8B5E00', border: '#F0A500' },
  pending:        { bg: '#F5F0E8', color: '#666666', border: '#D5CFC4' },
  active:         { bg: '#E6F4F1', color: '#1D6B6B', border: '#2A7F7F' },
  discharged:     { bg: '#F5F0E8', color: '#666666', border: '#D5CFC4' },
  finalized:      { bg: '#E8F0F7', color: '#1F4D78', border: '#2E74B5' },
  payment_failed: { bg: '#FCE8E8', color: '#B03A3A', border: '#D9534F' },
  voided:         { bg: '#EDEDED', color: '#666666', border: '#BDBDBD' },
  uncollectible:  { bg: '#EDEDED', color: '#666666', border: '#BDBDBD' },
}

// ── Assessments API error mapping ─────────────────────────────────────────────
export const mapApiError = (err, ctx = {}) => {
  switch (err?.status) {
    case 400: return { kind: 'validation', message: err.message || 'Please check the form and try again.' }
    case 403: return { kind: 'forbidden',  message: ctx.forbidden || 'You do not have access to this.' }
    case 404: return { kind: 'not_found',  message: ctx.notFound || 'Not found.' }
    case 409: return { kind: 'conflict',   message: ctx.conflict || err.message || 'This request conflicts with the current state.' }
    case 410: return { kind: 'gone',       message: ctx.gone || 'This assessment link has expired.' }
    default:  return { kind: 'server',     message: err?.message || 'Something went wrong. Please try again.' }
  }
}

// ── Assessment analytics (no PHI) ─────────────────────────────────────────────
export const trackAssessmentEvent = (name, payload = {}) => {
  try {
    window.dispatchEvent(new CustomEvent('lumina:analytics', {
      detail: { name, ...payload },
    }))
  } catch {}
}

// ── Next renewal date ─────────────────────────────────────────────────────────
export const nextRenewal = (anchorMonth) => {
  if (!anchorMonth) return '—'
  const now = new Date()
  const thisYear = now.getFullYear()
  const anchor = new Date(thisYear, anchorMonth - 1, 1)
  if (anchor <= now) anchor.setFullYear(thisYear + 1)
  return anchor.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}
